import { useMemo } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useTripJson, useWeather } from '../hooks/data.js';

export default function WhaleWatching() {
  const { tripId, trip } = useTrip();
  const data = useTripJson(tripId, 'whale-watching.json');
  const lat = trip?.destination?.lat ?? 58.3019;
  const lng = trip?.destination?.lng ?? -134.4197;
  const tz = trip?.dates?.timezone ?? 'America/Juneau';
  const weather = useWeather({ lat, lng, hours: 168, timezone: tz });

  const tripMornings = useMemo(() => {
    if (!trip?.dates?.start || !trip?.dates?.end) return [];
    return cruiseDays(trip.dates.start, trip.dates.end, tz);
  }, [trip?.dates?.start, trip?.dates?.end, tz]);

  const scoring = data.data?.scoring;

  const scored = useMemo(() => {
    if (!weather.data || !scoring) return tripMornings.map((d) => ({ ...d, score: null }));
    return tripMornings.map((day) => ({
      ...day,
      score: scoreMorning(day, weather.data, scoring),
    }));
  }, [tripMornings, weather.data, scoring]);

  const best =
    scored.find((s) => s.score?.label === 'calm') ||
    scored.find((s) => s.score?.label === 'manageable') ||
    null;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          decision support · marine
        </p>
        <h1 className="mt-1 font-serif text-2xl">Whale watching</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Five operators ranked by boat type and price. Compare the morning forecast across trip
          days — the calmest morning wins.
        </p>
      </header>

      <section
        aria-labelledby="forecast-compare"
        className="card overflow-hidden"
      >
        <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-3">
          <h2
            id="forecast-compare"
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
          >
            morning forecast (08:00–12:00 local)
          </h2>
          <p className="mt-1 font-serif text-base">
            {best?.score
              ? `${best.label} looks ${best.score.label}`
              : 'forecast loads closer to trip dates'}
          </p>
        </header>
        <ul className="divide-y divide-border-tertiary">
          {scored.map((day) => (
            <ForecastRow key={day.iso} day={day} isBest={day === best} />
          ))}
          {scored.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-fg-secondary">
              {weather.loading ? '…loading marine forecast' : 'No trip mornings configured.'}
            </li>
          )}
        </ul>
        {weather.error && (
          <p className="border-t border-border-tertiary px-4 py-2 font-mono text-[10px] text-danger">
            weather unavailable — {weather.error.message}
          </p>
        )}
      </section>

      {data.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading operators
        </p>
      )}
      {data.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          operators unavailable — {data.error.message}
        </div>
      )}

      <ul className="space-y-3">
        {(data.data?.operators || []).map((op) => (
          <OperatorCard key={op.id} op={op} />
        ))}
      </ul>

      {data.data?.notes && (
        <p className="px-1 text-xs italic text-fg-tertiary">{data.data.notes}</p>
      )}
    </div>
  );
}

function ForecastRow({ day, isBest }) {
  const score = day.score;
  return (
    <li
      className={[
        'flex items-baseline justify-between gap-3 px-4 py-3',
        isBest ? 'bg-action/5' : '',
      ].join(' ')}
    >
      <div className="min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
          {day.label}
          {isBest && (
            <span className="ml-2 rounded-full border border-action/40 bg-action/10 px-1.5 py-0.5 text-[10px] text-action">
              pick this morning
            </span>
          )}
        </p>
        {score ? (
          <p className="mt-1 text-sm text-fg-primary">{score.summary}</p>
        ) : (
          <p className="mt-1 font-serif text-sm italic text-fg-tertiary">
            forecast not yet in range
          </p>
        )}
      </div>
      {score && (
        <div className="text-right font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          <p>wind {score.windKt}kt</p>
          <p>gust {score.gustKt}kt</p>
          <p>precip {score.precipPct}%</p>
        </div>
      )}
    </li>
  );
}

function OperatorCard({ op }) {
  return (
    <li className="card overflow-hidden">
      <header className="space-y-1 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-serif text-lg leading-tight">{op.name}</h2>
          <p className="text-right">
            <span className="font-mono text-sm">${op.base_price_per_person}</span>
            <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              /person
            </span>
          </p>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {op.boat_type} · max {op.max_passengers} · {op.duration_hours}h · group ≈ ${op.group_total_for_3}
        </p>
      </header>
      <div className="space-y-3 px-4 py-3">
        {op.best_for && (
          <p className="font-serif italic text-sm text-fg-primary">{op.best_for}</p>
        )}
        {op.highlights?.length > 0 && (
          <ul className="space-y-1 text-sm text-fg-secondary">
            {op.highlights.map((h) => (
              <li key={h} className="flex gap-2">
                <span className="text-fg-tertiary" aria-hidden>
                  ◦
                </span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}
        {op.includes?.length > 0 && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            includes:{' '}
            <span className="normal-case tracking-normal text-fg-secondary">
              {op.includes.join(' · ')}
            </span>
          </p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          departs:{' '}
          <span className="normal-case tracking-normal text-fg-secondary">{op.departs_from}</span>
        </p>
        <BookingFooter op={op} />
      </div>
    </li>
  );
}

function BookingFooter({ op }) {
  if (op.booking_url) {
    return (
      <a
        href={op.booking_url}
        target="_blank"
        rel="noreferrer noopener"
        className="block w-full rounded-md border border-action bg-action px-3 py-2 text-center font-mono text-[11px] uppercase tracking-wider text-action-fg transition hover:opacity-90"
      >
        book {op.name}
      </a>
    );
  }
  return (
    <p className="rounded-md border border-dashed border-border-tertiary bg-bg-secondary px-3 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
      booking via aggregator · TBC
    </p>
  );
}

// Build the cruise-eligible mornings inside the trip window. Tuesday's
// arrival is mid-morning; we still include the day even though that
// morning is travel — closer to the trip the user might pivot. The
// operator decision is per-morning anyway. We exclude the final day
// (Friday departs late afternoon, packing eats the morning).
function cruiseDays(startISO, endISO, tz) {
  // Anchor at 12:00 UTC so any zone west of UTC (Juneau is UTC-8/-9)
  // formats to the intended local date — midnight-UTC drifts a day west.
  const start = new Date(`${startISO}T12:00:00Z`);
  const end = new Date(`${endISO}T12:00:00Z`);
  const out = [];
  const day = new Date(start);
  while (day < end) {
    out.push({
      iso: ymdInZone(day, tz),
      label: day.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: tz,
      }),
    });
    day.setUTCDate(day.getUTCDate() + 1);
  }
  return out;
}

function ymdInZone(d, tz) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const day = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${day}`;
}

// Score a morning window 08:00–12:00 local. Returns null if the
// forecast doesn't reach that morning.
function scoreMorning(day, weatherData, scoring) {
  if (!weatherData?.hourly?.time?.length) return null;
  const startMs = Date.parse(`${day.iso}T08:00:00`);
  const endMs = Date.parse(`${day.iso}T12:00:00`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;

  const winds = [];
  const gusts = [];
  const precips = [];
  for (let i = 0; i < weatherData.hourly.time.length; i += 1) {
    const t = Date.parse(weatherData.hourly.time[i]);
    if (Number.isNaN(t)) continue;
    if (t < startMs || t > endMs) continue;
    const w = weatherData.hourly.wind_speed_10m?.[i];
    const g = weatherData.hourly.wind_gusts_10m?.[i];
    const p = weatherData.hourly.precipitation_probability?.[i];
    if (typeof w === 'number') winds.push(w);
    if (typeof g === 'number') gusts.push(g);
    if (typeof p === 'number') precips.push(p);
  }
  if (winds.length === 0) return null;

  const avgWindMph = avg(winds);
  const peakGustMph = Math.max(...gusts, 0);
  const avgPrecip = avg(precips);

  // /api/weather pulls Open-Meteo with US-grid defaults (mph for wind).
  // Convert to knots for the scoring thresholds in the JSON config.
  const windKt = Math.round(avgWindMph * 0.8689);
  const gustKt = Math.round(peakGustMph * 0.8689);
  const precipPct = Math.round(avgPrecip);

  let label;
  if (
    windKt <= scoring.ideal_wind_kt &&
    gustKt <= scoring.ideal_gust_kt &&
    precipPct <= scoring.ideal_precip_probability
  ) {
    label = 'calm';
  } else if (
    windKt <= scoring.tolerable_wind_kt &&
    gustKt <= scoring.tolerable_gust_kt &&
    precipPct <= scoring.tolerable_precip_probability
  ) {
    label = 'manageable';
  } else {
    label = 'choppy';
  }

  const summary =
    label === 'calm'
      ? 'Light winds, low spray, comfortable for small boats.'
      : label === 'manageable'
        ? 'Some chop expected; pack motion-sickness pills.'
        : 'Rough morning — go later or pick a bigger boat.';

  return { label, summary, windKt, gustKt, precipPct };
}

function avg(arr) {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}
